import { Store } from "Application/Store/Store";
import { ihm_viewer__dictionary, locale } from "../../Application/i18n/i18nViewer";
import { usePlayer } from "./hooks/usePlayer";
import { SpeedIcon, LoopIcon } from "./components/icons/PlayerIcons";
import { calculateNumberPosition } from "./utils/playerMath";
import { pauseAnimation } from "./utils/animationState";

export { pauseAnimation };

/**
 * Animation Player
 */
export const Player = (times) => {
    const {
        playing,
        speed,
        loop,
        leftValue,
        keyFrame,
        isSceneLoaded,
        displayable,
        treatmentPlanningCounter,
        maxiStep,
        mandiStep,
        upperWidth,
        lowerWidth,
        largestWidth,
        maxSteps,
        numberFirstPosition,
        numberMaxiPosition,
        numberMandiPosition,
        isNearFirst,
        isNearMaxi,
        isNearMandi,
        hasAnimation,
        togglePlay,
        changeFrame,
        changeSpeed,
        changeLoop,
        prevFrame,
        nextFrame,
        handleTimelineMouseDown,
    } = usePlayer(times);

    if (displayable !== "flex" || !hasAnimation) {
        return null;
    }

    const upperKeyframe = keyFrame > maxiStep ? maxiStep : keyFrame;
    const lowerKeyframe = keyFrame > mandiStep ? mandiStep : keyFrame;

    const upperThumbLeft =
        (calculateNumberPosition(upperKeyframe, maxSteps, largestWidth) / 100) *
        largestWidth;

    const lowerThumbLeft =
        (calculateNumberPosition(lowerKeyframe, maxSteps, largestWidth) / 100) *
        largestWidth;

    const playerIsHidden = !isSceneLoaded;
    const isAtFrameZero = parseInt(keyFrame, 10) === 0;
    const animationIncrement = Store.getState().AnimationReducer.increment;
    const nbMaxi = Store.getState().AnimationReducer.nbMaxi;
    const nbMandi = Store.getState().AnimationReducer.nbMandi;
    const hasNoIncrement = animationIncrement === 0;
    const shouldShowMaxiCounter = animationIncrement !== 0 && animationIncrement > nbMaxi;
    const shouldShowMandiCounter = animationIncrement !== 0 && animationIncrement > nbMandi;

    return (
        <>
            <nav
                id="Player"
                style={{ display: displayable }}
                className={`glass-panel${playerIsHidden ? " hidden" : ""}`}
            >
                <div id="PlayerButtonsContainer">
                    <button onClick={() => changeFrame(prevFrame(keyFrame))}>
                        <svg viewBox="-10 0 64 64" style={{ transform: "rotate(180deg)" }}>
                            <polygon points="64.385,31.891 32.095,7.365 32.095,32.343 0,7.967 0,57.02 32.095,32.644 32.095,56.417" />
                        </svg>
                    </button>
                    <button id="playButton" onClick={togglePlay}>
                        {playing && (
                            <svg viewBox="0 0 20 20">
                                <rect width="6" height="16" x="3" y="2" rx="1" ry="1" />
                                <rect width="6" height="16" x="11" y="2" rx="1" ry="1" />
                            </svg>
                        )}
                        {!playing && (
                            <svg viewBox="0 0 320 320">
                                <path d="M295.84,146.049l-256-144c-4.96-2.784-11.008-2.72-15.904,0.128C19.008,5.057,16,10.305,16,16.001v288 c0,5.696,3.008,10.944,7.936,13.824c2.496,1.44,5.28,2.176,8.064,2.176c2.688,0,5.408-0.672,7.84-2.048l256-144 c5.024-2.848,8.16-8.16,8.16-13.952S300.864,148.897,295.84,146.049z" />
                            </svg>
                        )}
                    </button>
                    <button onClick={() => changeFrame(nextFrame(keyFrame))}>
                        <svg viewBox="-5 0 64 64">
                            <polygon points="64.385,31.891 32.095,7.365 32.095,32.343 0,7.967 0,57.02 32.095,32.644 32.095,56.417" />
                        </svg>
                    </button>
                    <button id="speedButton" onClick={changeSpeed}>
                        <SpeedIcon speed={speed} />
                    </button>
                </div>

                <div id="PlayerTimelineContainers">
                    <div
                        id="PlayerTimelineContainer"
                        style={{
                            width: `${largestWidth}px`,
                            position: "relative",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <div
                            className="custom-timeline"
                            style={{
                                width: `${upperWidth}px`,
                                height: "10px",
                                backgroundImage: "linear-gradient(to bottom, #FFF, #999, #FFF)",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "100% 25%",
                                position: "relative",
                                cursor: "pointer",
                            }}
                            onMouseDown={(e) => handleTimelineMouseDown("upper", e)}
                        >
                            <div
                                className="custom-thumb"
                                style={{
                                    position: "absolute",
                                    left: `${upperThumbLeft}px`,
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    padding: "0.25px",
                                    boxShadow: "0 0 .5em #FFF inset",
                                    background:
                                        "linear-gradient(#888, #FFF) content-box, linear-gradient(-90deg, #888, #DDD) border-box",
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                    </div>

                    <div id="NomberContainer" style={{ width: `${largestWidth}px` }}>
                        <output
                            id="timelineOutput"
                            style={{
                                left: `${leftValue}%`,
                                transform: "translateX(-50%)",
                            }}
                        >
                        <span>
                            {isAtFrameZero ? 0 : parseInt(keyFrame, 10) + animationIncrement}
                        </span>
                        </output>
                        <span
                            id="NomberFirst"
                            style={{
                                position: "absolute",
                                left: `${numberFirstPosition}%`,
                                transform: "translateX(-50%)",
                                opacity: isNearFirst ? 0 : 1,
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            0
                        </span>
                        <span
                            id="NbMaxiWhenIncrement"
                            style={{
                                position: "absolute",
                                left: `${numberMaxiPosition}%`,
                                top: "-25px",
                                transform: "translateX(-50%)",
                            }}
                        >
                            {shouldShowMaxiCounter ? nbMaxi : ""}
                        </span>
                        <span
                            id="NbMandiWhenIncrement"
                            style={{
                                position: "absolute",
                                left: `${numberMandiPosition}%`,
                                top: "25px",
                                transform: "translateX(-50%)",
                            }}
                        >
                            {shouldShowMandiCounter ? nbMandi : ""}
                        </span>
                        <span
                            id="NomberMaxi"
                            style={{
                                position: "absolute",
                                left: `${numberMaxiPosition}%`,
                                transform: "translateX(-50%)",
                                opacity: isNearMaxi ? 0 : 1,
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            {maxiStep + animationIncrement}
                        </span>
                        <span
                            id="NomberMandi"
                            style={{
                                position: "absolute",
                                left: `${numberMandiPosition}%`,
                                transform: "translateX(-50%)",
                                opacity: isNearMandi ? 0 : 1,
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            {mandiStep + animationIncrement}
                        </span>
                    </div>

                    <div
                        id="PlayerTimelineContainer2"
                        style={{
                            width: `${largestWidth}px`,
                            position: "relative",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <div
                            className="custom-timeline"
                            style={{
                                width: `${lowerWidth}px`,
                                height: "10px",
                                backgroundImage: "linear-gradient(to bottom, #FFF, #999, #FFF)",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "100% 25%",
                                position: "relative",
                                cursor: "pointer",
                            }}
                            onMouseDown={(e) => handleTimelineMouseDown("lower", e)}
                        >
                            <div
                                className="custom-thumb"
                                style={{
                                    position: "absolute",
                                    left: `${lowerThumbLeft}px`,
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    padding: "0.25px",
                                    boxShadow: "0 0 .5em #FFF inset",
                                    background:
                                        "linear-gradient(#888, #FFF) content-box, linear-gradient(-90deg, #888, #DDD) border-box",
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div id="PlayerResetContainer">
                    <button onClick={() => changeFrame(0)}>
                        <svg viewBox="-10 0 64 64" style={{ transform: "rotate(180deg)" }}>
                            <polygon points="64.385,31.891 32.095,7.365 32.095,32.343 0,7.967 0,57.02 32.095,32.644 32.095,56.417" />
                        </svg>
                    </button>
                </div>

                <div id="loopContainer">
                    <button id="loopButton" onClick={changeLoop}>
                        <LoopIcon loop={loop} />
                    </button>
                </div>

                <span
                    id="PlayerStepCounter"
                    style={{ display: displayable }}
                    className={playerIsHidden ? "hidden" : null}
                >
                    {ihm_viewer__dictionary.SPE_MEASURES_ITEMS.UPPER[locale()]}:{" "}
                    {treatmentPlanningCounter.upper === 0 ? 0 : hasNoIncrement ? treatmentPlanningCounter.upper : nbMaxi}{" "}
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    {ihm_viewer__dictionary.SPE_MEASURES_ITEMS.LOWER[locale()]}:{" "}
                    {treatmentPlanningCounter.lower === 0 ? 0 : hasNoIncrement ? treatmentPlanningCounter.lower : nbMandi}
                </span>
            </nav>
        </>
    );
};
